import { ReactNode } from 'react';
import { useAuthorities } from '@/hooks/useAuthorities';
import { Authority } from '@/types/auth';
import { Alert, AlertDescription } from '@/components/_components/alert';

interface PageAccessGuardProps {
  children: ReactNode;
  /** Single authority required to access the page */
  authority?: Authority;
  /** Multiple authorities - user needs ANY of these */
  anyOf?: Authority[];
  /** Multiple authorities - user needs ALL of these */
  allOf?: Authority[];
  /** Custom fallback component */
  fallback?: ReactNode;
}

/**
 * Guard component for entire pages
 * Shows access denied message if user doesn't have required authority
 * 
 * @example
 * function CampaignPage() {
 *   return (
 *     <PageAccessGuard authority="ADMIN_CAMPAIGN_MGMT_VIEW">
 *       <CampaignContent />
 *     </PageAccessGuard>
 *   );
 * }
 */
export function PageAccessGuard({
  children,
  authority,
  anyOf,
  allOf,
  fallback,
}: PageAccessGuardProps) {
  const { hasAuthority, hasAnyAuthority, hasAllAuthorities } = useAuthorities();

  let hasPermission = false;

  if (authority) {
    hasPermission = hasAuthority(authority);
  } else if (anyOf && anyOf.length > 0) {
    hasPermission = hasAnyAuthority(...anyOf);
  } else if (allOf && allOf.length > 0) {
    hasPermission = hasAllAuthorities(...allOf);
  } else {
    // No authority specified, allow by default
    hasPermission = true;
  }

  if (!hasPermission) {
    if (fallback) {
      return <>{fallback}</>;
    }

    return (
      <div className="flex items-center justify-center min-h-[400px] p-4">
        <Alert variant="destructive" className="max-w-md">
          <AlertDescription className="text-center">
            <div className="mb-2">
              <svg
                className="mx-auto h-12 w-12 text-red-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <h3 className="font-semibold text-lg mb-1">Access Denied</h3>
            <p>You don't have permission to access this page.</p>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return <>{children}</>;
}
